import { useMemo } from 'react'

import { TreeItemIndex, TreeItem } from 'react-complex-tree'

import { MESSAGING_POST_TYPE_SLUG, sortByName } from '@bettermode/common/utils'
import { PostTypeContext } from '@tribeplatform/gql-client/types'
import { useCMS } from '@tribeplatform/react-components/CMS'
import { useI18n } from '@tribeplatform/react-components/i18n'

import {
  ARCHIVED_CMS_INDEX,
  createArchivedCMSRootTreeItem,
} from '../tree-items/createArchivedCMSRootTreeItem.js'
import {
  CMS_INDEX,
  createCMSRootTreeItem,
} from '../tree-items/createCMSRootTreeItem.js'
import {
  createCMSTreeItem,
  getCMSIndex,
} from '../tree-items/createCMSTreeItem.js'
import { TreeItemData, TreeItems } from '../types.js'

export function useCMSTreeItems({ query }: { query?: string }) {
  const intl = useI18n()
  const { allActivePostTypes, allActiveReplyTypes, allArchivedCMSTypes } =
    useCMS()

  const topLevelPostTypes = useMemo(() => {
    return [...allActivePostTypes, ...allActiveReplyTypes]
  }, [allActivePostTypes, allActiveReplyTypes])

  const foundPostTypes = useMemo(
    () =>
      sortByName(
        query
          ? topLevelPostTypes.filter(postType => {
              return postType.name?.toLowerCase().includes(query.toLowerCase())
            })
          : topLevelPostTypes,
      ),
    [query, topLevelPostTypes],
  )

  const foundArchivedPostTypes = useMemo(() => {
    return sortByName(
      allArchivedCMSTypes.filter(postType => {
        return postType.name?.toLowerCase().includes(query?.toLowerCase())
      }),
    )
  }, [allArchivedCMSTypes, query])

  const cmsRootItem = useMemo(() => {
    const children = foundPostTypes.map(postType => getCMSIndex(postType.id))
    if (foundArchivedPostTypes.length > 0) {
      children.push(ARCHIVED_CMS_INDEX)
    }
    return {
      [CMS_INDEX]: createCMSRootTreeItem(intl, {
        children,
      }),
    }
  }, [intl, foundArchivedPostTypes, foundPostTypes])

  const archivedCmsRootItem = useMemo(() => {
    const children = foundArchivedPostTypes.map(postType =>
      getCMSIndex(postType.id),
    )
    return {
      [ARCHIVED_CMS_INDEX]: createArchivedCMSRootTreeItem(intl, {
        children,
      }),
    }
  }, [intl, foundArchivedPostTypes])

  const treeItems: Record<
    TreeItemIndex,
    TreeItem<TreeItemData>
  > = useMemo((): TreeItems => {
    const postTypes = [...foundPostTypes, ...foundArchivedPostTypes]
      .filter(it => it.context === PostTypeContext.post)
      .filter(it => it.slug !== MESSAGING_POST_TYPE_SLUG)
    return postTypes.reduce((acc, postType) => {
      return { ...acc, ...createCMSTreeItem({ postType }) }
    }, {})
  }, [foundArchivedPostTypes, foundPostTypes])

  const cmsTreeItems = useMemo(() => {
    return {
      ...cmsRootItem,
      ...archivedCmsRootItem,
      ...treeItems,
    }
  }, [archivedCmsRootItem, cmsRootItem, treeItems])

  return {
    cmsTreeItems,
    cmsIndex: CMS_INDEX,
  }
}
